import React, { useState, useEffect, useRef } from 'react';
import SEO from '../../components/common/SEO';
import { useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import { COMPANY } from '../../config/company';
import QRWidget from '../../components/common/QRWidget';

/* ─── Design tokens ─────────────────────────────────────── */
const C = {
  navy: '#0a1628', blue: '#1d4ed8', blueLt: '#eff6ff', blueMd: '#dbeafe',
  text: '#0f172a', sub: '#475569', muted: '#94a3b8', border: '#e2e8f0',
  bg: '#f8fafc', white: '#ffffff', green: '#059669', teal: '#0891b2',
};

/* ─── Scroll-reveal ──────────────────────────────────────── */
function FadeIn({ children, delay = 0, direction = 'up' }) {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVis(true); }, { threshold: 0.08 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  const tx = direction === 'up' ? '0,24px,0' : direction === 'left' ? '24px,0,0' : '-24px,0,0';
  return (
    <div ref={ref} style={{
      transition: `opacity 0.6s ease ${delay}ms, transform 0.6s ease ${delay}ms`,
      opacity: vis ? 1 : 0,
      transform: vis ? 'translate3d(0,0,0)' : `translate3d(${tx})`,
    }}>{children}</div>
  );
}

/* ─── FAQ data ───────────────────────────────────────────── */
const FAQS = [
  {
    q: 'What is your minimum order quantity?',
    a: 'We supply compounds from as little as 1 mg. For custom synthesis projects, MOQ depends on complexity and scale — typically 5–10 mg for novel impurities.',
  },
  {
    q: 'Do you provide a Certificate of Analysis?',
    a: 'Yes, every batch ships with a lot-specific CoA including HPLC purity, NMR, LC-MS, and IR data. Available as PDF on request.',
  },
  {
    q: 'How long does custom synthesis take?',
    a: 'Standard catalog items: 1–3 weeks. Custom synthesis and novel impurities: 4–8 weeks depending on complexity. Rush timelines available — contact us.',
  },
  {
    q: 'Which pharmacopoeial standards do you follow?',
    a: 'We comply with ICH Q3A/Q3B/M7, USP, EP, and BP guidelines. Our compounds support FDA, EMA, CDSCO, and TGA regulatory submissions.',
  },
  {
    q: 'Do you ship internationally?',
    a: 'Yes. We export to 30+ countries with proper documentation — SDS, CoA, import permits where required. Experienced with DHL, FedEx cold-chain logistics.',
  },
  {
    q: 'Can you synthesize stable isotope-labelled compounds?',
    a: 'Yes, we specialize in deuterium (D) and ¹³C-labelled compounds for ADME studies, bioanalytical method development, and quantitation as internal standards.',
  },
];

function FAQItem({ q, a, delay }) {
  const [open, setOpen] = useState(false);
  return (
    <FadeIn delay={delay}>
      <div style={{ border: `1px solid ${open ? C.blue + '35' : C.border}`, borderRadius: 12, overflow: 'hidden', transition: 'border-color 0.2s' }}>
        <button onClick={() => setOpen(!open)}
          style={{ width: '100%', textAlign: 'left', padding: '16px 20px', background: open ? C.blueLt : C.white, border: 'none', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, transition: 'background 0.2s' }}>
          <span style={{ fontWeight: 700, fontSize: '0.9rem', color: open ? C.blue : C.text, lineHeight: 1.4 }}>{q}</span>
          <svg width="18" height="18" fill="none" stroke={open ? C.blue : C.muted} strokeWidth="2.5" viewBox="0 0 24 24"
            style={{ flexShrink: 0, transition: 'transform 0.25s', transform: open ? 'rotate(180deg)' : 'none' }}>
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </button>
        {open && (
          <div style={{ padding: '0 20px 18px', fontSize: '0.875rem', color: C.sub, lineHeight: 1.8, background: C.white }}>
            {a}
          </div>
        )}
      </div>
    </FadeIn>
  );
}

/* ─── Quick action buttons ───────────────────────────────── */
const QUICK_ACTIONS = [
  {
    icon: <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>,
    label: 'Send Email',
    sub: COMPANY.email,
    color: C.blue,
    bg: C.blueLt,
    href: `mailto:${COMPANY.email}`,
  },
  {
    icon: <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.01 1.24a2 2 0 012-2.18h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 7.09a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 14z"/></svg>,
    label: 'Call Us',
    sub: COMPANY.phone,
    color: C.green,
    bg: '#f0fdf4',
    href: `tel:${COMPANY.phone}`,
  },
  {
    icon: <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>,
    label: 'WhatsApp',
    sub: 'Chat directly',
    color: '#25d366',
    bg: '#f0fdf4',
    href: `https://wa.me/${(COMPANY.phone || '').replace(/\D/g, '')}`,
  },
  {
    icon: <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>,
    label: 'Our Office',
    sub: COMPANY.address?.city || 'Hyderabad',
    color: '#7c3aed',
    bg: '#f5f3ff',
    href: 'https://maps.google.com/?q=PRASI+Labs+Hyderabad',
  },
];

/* ─── Info cards ─────────────────────────────────────────── */
const INFO_CARDS = [
  {
    icon: <svg width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>,
    title: 'Registered Address',
    color: C.blue,
    bg: C.blueLt,
    content: (
      <div>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>{COMPANY.name}</div>
        <div>{COMPANY.address?.line1}</div>
        <div>{COMPANY.address?.line2}</div>
        <div>{COMPANY.address?.city} – {COMPANY.address?.pin}</div>
        <div>{COMPANY.address?.state}, {COMPANY.address?.country}</div>
      </div>
    ),
  },
  {
    icon: <svg width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>,
    title: 'Email',
    color: C.teal,
    bg: '#ecfeff',
    content: (
      <div>
        <a href={`mailto:${COMPANY.email}`} style={{ color: C.teal, fontWeight: 600, textDecoration: 'none' }}>{COMPANY.email}</a>
        <div style={{ marginTop: 6, fontSize: '0.82rem' }}>We respond within 24 business hours</div>
      </div>
    ),
  },
  {
    icon: <svg width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.01 1.24a2 2 0 012-2.18h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 7.09a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 14z"/></svg>,
    title: 'Phone / WhatsApp',
    color: C.green,
    bg: '#f0fdf4',
    content: (
      <div>
        <a href={`tel:${COMPANY.phone}`} style={{ color: C.green, fontWeight: 600, textDecoration: 'none' }}>{COMPANY.phone}</a>
        <div style={{ marginTop: 4, fontSize: '0.82rem' }}>{COMPANY.hours}</div>
        <div style={{ fontSize: '0.82rem' }}>Sunday Closed</div>
      </div>
    ),
  },
];

/* ═══════════════════════════════════════════════════════════ */
export default function Contact() {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({
    name: '', company: '', email: '', phone: '',
    subject: '', message: '', product_interest: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState('');
  const [focusField, setFocusField] = useState('');

  useEffect(() => {
    const product = searchParams.get('product') || '';
    const name = searchParams.get('name') || '';
    setForm(f => ({
      ...f,
      product_interest: name || product,
      subject: name ? `Quote Request: ${name}` : product ? `Quote Request: ${product}` : '',
    }));
  }, [searchParams]);

  const handleChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSubmitting(true);
    try {
      const { data } = await api.post('/public/contact', form);
      setSuccess(data.inquiry_number);
    } catch (err) {
      setError(err.response?.data?.message || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = (name) => ({
    width: '100%', padding: '10px 14px', borderRadius: 10,
    border: `1.5px solid ${focusField === name ? C.blue : C.border}`,
    fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box',
    transition: 'border-color 0.18s, box-shadow 0.18s',
    boxShadow: focusField === name ? `0 0 0 3px ${C.blue}18` : 'none',
    fontFamily: 'inherit',
  });

  const labelStyle = { display: 'block', fontSize: '0.78rem', fontWeight: 700, color: C.text, marginBottom: 6, letterSpacing: '0.01em' };

  if (success) {
    return (
      <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '60px 24px' }}>
        <div style={{ maxWidth: 520, width: '100%', background: C.white, borderRadius: 22, padding: '52px 44px', boxShadow: '0 12px 40px rgba(0,0,0,0.1)', border: `1px solid ${C.border}`, textAlign: 'center' }}>
          {/* Animated success circle */}
          <div style={{ position: 'relative', width: 72, height: 72, margin: '0 auto 24px' }}>
            <div style={{ width: 72, height: 72, background: '#d1fae5', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="34" height="34" fill="none" stroke="#059669" strokeWidth="2.5" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>
            </div>
          </div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 900, color: C.text, marginBottom: 12 }}>Inquiry Received!</h2>
          <p style={{ color: C.sub, marginBottom: 24, lineHeight: 1.75 }}>
            Thank you for reaching out. Our scientific team will review your inquiry and respond within <strong>24 business hours</strong>.
          </p>
          <div style={{ background: C.bg, borderRadius: 12, padding: '16px 24px', border: `1px solid ${C.border}`, display: 'inline-block', marginBottom: 28 }}>
            <div style={{ fontSize: '0.72rem', color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Inquiry Reference</div>
            <div style={{ fontWeight: 800, color: C.blue, fontFamily: 'monospace', fontSize: '1.1rem', letterSpacing: '0.04em' }}>{success}</div>
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => { setSuccess(null); setForm({ name: '', company: '', email: '', phone: '', subject: '', message: '', product_interest: '' }); }}
              style={{ padding: '10px 24px', background: C.blue, color: '#fff', borderRadius: 9, border: 'none', fontWeight: 700, cursor: 'pointer', fontSize: '0.875rem' }}>
              Submit Another
            </button>
            <a href="/" style={{ padding: '10px 24px', background: C.bg, color: C.text, borderRadius: 9, border: `1px solid ${C.border}`, fontWeight: 600, cursor: 'pointer', fontSize: '0.875rem', textDecoration: 'none', display: 'inline-block' }}>
              Back to Home
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
    <SEO
      title="Contact PRASI LABS — Request a Quote for Reference Standards"
      description="Contact PRASI LABS in Hyderabad, India to request a quote for pharmaceutical reference standards, drug impurity standards, or custom synthesis services. Call +91-9160006515 or email info@prasilabs.com."
      keywords="contact PRASI LABS, request quote reference standards, pharmaceutical standards enquiry, buy drug impurity standards, custom synthesis enquiry, PRASI LABS Hyderabad contact"
      canonical="/contact"
      jsonLd={[
        {
          '@context': 'https://schema.org',
          '@type': 'ContactPage',
          name: 'Contact PRASI LABS',
          url: 'https://www.prasilabs.com/contact',
          description: 'Contact PRASI LABS to request a quote for pharmaceutical reference standards, drug impurities, or custom synthesis services.',
          publisher: { '@id': 'https://www.prasilabs.com/#organization' },
        },
        {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: FAQS.map(({ q, a }) => ({
            '@type': 'Question',
            name: q,
            acceptedAnswer: { '@type': 'Answer', text: a },
          })),
        },
      ]}
    />
    <div>
      <style>{`
        @keyframes contact-glow { 0%,100%{opacity:0.05} 50%{opacity:0.1} }
        @media (max-width: 768px) {
          .pub-contact-grid { grid-template-columns: 1fr !important; }
          .pub-contact-form { padding: 24px 20px !important; }
          .pub-contact-form-row { grid-template-columns: 1fr !important; }
          .pub-contact-wrap { padding: 32px 16px !important; }
          .pub-contact-hero { padding: 44px 16px 36px !important; }
        }
      `}</style>

      {/* ── Hero ─────────────────────────────────────────── */}
      <div style={{ background: 'linear-gradient(135deg, #0a1628 0%, #1e3a8a 55%, #1d4ed8 100%)', padding: 'clamp(44px,6vw,72px) clamp(16px,3vw,24px) clamp(40px,5vw,68px)', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <svg style={{ position: 'absolute', right: 60, top: '50%', transform: 'translateY(-50%)', animation: 'contact-glow 4s ease-in-out infinite' }} width="260" height="220" viewBox="0 0 260 220">
          <circle cx="65" cy="110" r="32" stroke="white" strokeWidth="2.5" fill="none"/>
          <circle cx="130" cy="50" r="32" stroke="white" strokeWidth="2.5" fill="none"/>
          <circle cx="195" cy="110" r="32" stroke="white" strokeWidth="2.5" fill="none"/>
          <circle cx="130" cy="170" r="32" stroke="white" strokeWidth="2.5" fill="none"/>
          <line x1="94" y1="90" x2="108" y2="72" stroke="white" strokeWidth="2.5"/>
          <line x1="152" y1="72" x2="166" y2="90" stroke="white" strokeWidth="2.5"/>
          <line x1="166" y1="130" x2="152" y2="148" stroke="white" strokeWidth="2.5"/>
          <line x1="108" y1="148" x2="94" y2="130" stroke="white" strokeWidth="2.5"/>
        </svg>
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'inline-block', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.18)', borderRadius: 20, padding: '4px 16px', fontSize: '0.7rem', fontWeight: 700, color: '#93c5fd', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 18 }}>
            Get in Touch
          </div>
          <h1 style={{ fontSize: 'clamp(1.9rem, 4.5vw, 3rem)', fontWeight: 900, color: '#fff', marginBottom: 14, letterSpacing: '-0.5px' }}>Contact PRASI LABS</h1>
          <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: '1rem', maxWidth: 500, margin: '0 auto 32px', lineHeight: 1.75 }}>
            Request a quote, enquire about availability, or discuss your custom synthesis requirements with our scientific team.
          </p>
          {/* Quick stat pills */}
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            {[['⚡', '24hr response'], ['🌍', '30+ countries'], ['🧪', 'Custom synthesis'], ['📋', 'Free CoA']].map(([icon, label]) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.18)', borderRadius: 20, padding: '5px 14px', fontSize: '0.75rem', fontWeight: 600, color: 'rgba(255,255,255,0.9)' }}>
                <span>{icon}</span>{label}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Quick Actions ─────────────────────────────────── */}
      <div style={{ background: C.white, borderBottom: `1px solid ${C.border}` }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 24px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
          {QUICK_ACTIONS.map((action, i) => (
            <FadeIn key={action.label} delay={i * 60} direction="up">
              <a href={action.href} target={action.href.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer"
                style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 18px', background: C.bg, border: `1.5px solid ${C.border}`, borderRadius: 14, textDecoration: 'none', transition: 'all 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = action.color; e.currentTarget.style.background = action.bg; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = `0 6px 20px ${action.color}22`; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.background = C.bg; e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}>
                <div style={{ width: 42, height: 42, borderRadius: 11, background: action.bg, color: action.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {action.icon}
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '0.88rem', color: C.text }}>{action.label}</div>
                  <div style={{ fontSize: '0.75rem', color: C.muted, marginTop: 1 }}>{action.sub}</div>
                </div>
              </a>
            </FadeIn>
          ))}
        </div>
      </div>

      <div className="pub-contact-wrap" style={{ maxWidth: 1200, margin: '0 auto', padding: '56px 24px' }}>

        {/* ── Main grid: form + sidebar ──────────────────── */}
        <div className="pub-contact-grid" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.6fr) minmax(0, 1fr)', gap: 40, alignItems: 'start' }}>

          {/* ── Contact Form ───────────────────────────────── */}
          <FadeIn direction="left">
            <div className="pub-contact-form" style={{ background: C.white, borderRadius: 20, padding: '36px 40px', border: `1px solid ${C.border}`, boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
              <div style={{ marginBottom: 28 }}>
                <h2 style={{ fontSize: '1.3rem', fontWeight: 900, color: C.text, marginBottom: 6 }}>Send Us an Inquiry</h2>
                <p style={{ fontSize: '0.85rem', color: C.sub, lineHeight: 1.6 }}>Fill in the details below and our team will get back to you within 24 hours.</p>
              </div>

              {error && (
                <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 10, padding: '12px 16px', marginBottom: 20, color: '#dc2626', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                {/* Row 1: name + company */}
                <div className="pub-contact-form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                  <div>
                    <label style={labelStyle}>Full Name <span style={{ color: '#dc2626' }}>*</span></label>
                    <input name="name" value={form.name} onChange={handleChange} required
                      placeholder="Dr. Jane Smith" style={inputStyle('name')}
                      onFocus={() => setFocusField('name')} onBlur={() => setFocusField('')} />
                  </div>
                  <div>
                    <label style={labelStyle}>Company / Institution</label>
                    <input name="company" value={form.company} onChange={handleChange}
                      placeholder="Pharma Ltd." style={inputStyle('company')}
                      onFocus={() => setFocusField('company')} onBlur={() => setFocusField('')} />
                  </div>
                </div>

                {/* Row 2: email + phone */}
                <div className="pub-contact-form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                  <div>
                    <label style={labelStyle}>Email <span style={{ color: '#dc2626' }}>*</span></label>
                    <input type="email" name="email" value={form.email} onChange={handleChange} required
                      placeholder="jane@pharma.com" style={inputStyle('email')}
                      onFocus={() => setFocusField('email')} onBlur={() => setFocusField('')} />
                  </div>
                  <div>
                    <label style={labelStyle}>Phone</label>
                    <input name="phone" value={form.phone} onChange={handleChange}
                      placeholder="+91 98765 43210" style={inputStyle('phone')}
                      onFocus={() => setFocusField('phone')} onBlur={() => setFocusField('')} />
                  </div>
                </div>

                {/* Subject */}
                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle}>Subject <span style={{ color: '#dc2626' }}>*</span></label>
                  <input name="subject" value={form.subject} onChange={handleChange} required
                    placeholder="Quote Request / Product Inquiry / Custom Synthesis" style={inputStyle('subject')}
                    onFocus={() => setFocusField('subject')} onBlur={() => setFocusField('')} />
                </div>

                {/* Product of interest */}
                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle}>Product / Catalog Number</label>
                  <input name="product_interest" value={form.product_interest} onChange={handleChange}
                    placeholder="e.g. PL0719, Amlodipine Impurity A, CAS 88150-42-9" style={inputStyle('product_interest')}
                    onFocus={() => setFocusField('product_interest')} onBlur={() => setFocusField('')} />
                </div>

                {/* Message */}
                <div style={{ marginBottom: 24 }}>
                  <label style={labelStyle}>Message <span style={{ color: '#dc2626' }}>*</span></label>
                  <textarea name="message" value={form.message} onChange={handleChange} required rows={5}
                    placeholder="Describe your requirements — quantities needed, purity target, timeline, intended use (regulatory / research), etc."
                    style={{ ...inputStyle('message'), resize: 'vertical' }}
                    onFocus={() => setFocusField('message')} onBlur={() => setFocusField('')} />
                </div>

                {/* Submit */}
                <button type="submit" disabled={submitting}
                  style={{ width: '100%', padding: '13px', background: submitting ? '#93c5fd' : C.blue, color: '#fff', borderRadius: 11, fontWeight: 800, fontSize: '0.95rem', border: 'none', cursor: submitting ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9, transition: 'background 0.2s', boxShadow: `0 4px 18px ${C.blue}44` }}>
                  {submitting
                    ? <><div className="spinner" style={{ width: 16, height: 16 }} /> Sending…</>
                    : <>
                        Send Inquiry
                        <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                      </>
                  }
                </button>

                <p style={{ fontSize: '0.75rem', color: C.muted, marginTop: 12, textAlign: 'center' }}>
                  By submitting, you agree to be contacted by PRASI LABS regarding your inquiry.
                </p>
              </form>
            </div>
          </FadeIn>

          {/* ── Sidebar ────────────────────────────────────── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

            {/* Info cards */}
            {INFO_CARDS.map((card, i) => (
              <FadeIn key={card.title} delay={i * 80} direction="left">
                <div style={{ background: C.white, borderRadius: 16, padding: '20px 22px', border: `1px solid ${C.border}`, display: 'flex', gap: 16, alignItems: 'flex-start',
                  transition: 'all 0.2s' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = card.color + '40'; e.currentTarget.style.boxShadow = `0 6px 20px ${card.color}12`; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.boxShadow = 'none'; }}>
                  <div style={{ width: 46, height: 46, borderRadius: 12, background: card.bg, color: card.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {card.icon}
                  </div>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: '0.88rem', color: C.text, marginBottom: 8 }}>{card.title}</div>
                    <div style={{ fontSize: '0.82rem', color: C.sub, lineHeight: 1.7 }}>{card.content}</div>
                  </div>
                </div>
              </FadeIn>
            ))}

            {/* WhatsApp QR card */}
            <FadeIn delay={260} direction="left">
              <div style={{ background: '#f0fdf4', borderRadius: 16, padding: '20px 22px', border: '1px solid #bbf7d0', display: 'flex', gap: 18, alignItems: 'center' }}>
                <QRWidget
                  value={COMPANY.social.whatsapp}
                  size={88}
                  dark="#064e3b"
                  label="Scan for WhatsApp"
                />
                <div>
                  <div style={{ fontWeight: 800, fontSize: '0.9rem', color: '#064e3b', marginBottom: 6 }}>Chat on WhatsApp</div>
                  <div style={{ fontSize: '0.78rem', color: '#065f46', lineHeight: 1.6 }}>
                    Scan the QR code with your phone to start a WhatsApp conversation instantly — no need to type the number.
                  </div>
                </div>
              </div>
            </FadeIn>

            {/* Response time card */}
            <FadeIn delay={280} direction="left">
              <div style={{ background: 'linear-gradient(135deg, #0a1628, #1e3a8a)', borderRadius: 16, padding: '24px 26px', color: '#fff' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                  <span style={{ fontSize: '1.4rem' }}>⚡</span>
                  <div style={{ fontWeight: 800, fontSize: '1rem' }}>Quick Response Promise</div>
                </div>
                <p style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.78)', lineHeight: 1.75, marginBottom: 16 }}>
                  All inquiries answered within <strong style={{ color: '#93c5fd' }}>24 business hours</strong>. Technical queries reviewed by our scientific team. Quotes issued same day for catalog items.
                </p>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {['Mon–Fri', '9am–6pm IST', 'Quick replies'].map(t => (
                    <span key={t} style={{ fontSize: '0.7rem', fontWeight: 700, background: 'rgba(255,255,255,0.12)', borderRadius: 20, padding: '3px 10px', color: 'rgba(255,255,255,0.88)' }}>{t}</span>
                  ))}
                </div>
              </div>
            </FadeIn>
          </div>
        </div>

        {/* ── FAQ Section ───────────────────────────────────── */}
        <div style={{ marginTop: 72 }}>
          <FadeIn>
            <div style={{ textAlign: 'center', marginBottom: 44 }}>
              <div style={{ display: 'inline-block', background: C.blueLt, color: C.blue, fontSize: '0.7rem', fontWeight: 700, padding: '4px 14px', borderRadius: 20, marginBottom: 14, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                Common Questions
              </div>
              <h2 style={{ fontSize: 'clamp(1.5rem, 2.5vw, 2rem)', fontWeight: 900, color: C.text, marginBottom: 12 }}>Frequently Asked Questions</h2>
              <p style={{ fontSize: '0.95rem', color: C.sub, maxWidth: 500, margin: '0 auto', lineHeight: 1.7 }}>
                Answers to common questions about our products, services, and processes.
              </p>
            </div>
          </FadeIn>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(460px, 1fr))', gap: 12 }}>
            {FAQS.map((faq, i) => <FAQItem key={i} {...faq} delay={i * 50} />)}
          </div>
          <FadeIn delay={300}>
            <div style={{ marginTop: 32, textAlign: 'center', padding: '24px', background: C.blueLt, borderRadius: 14, border: `1px solid ${C.blueMd}` }}>
              <div style={{ fontWeight: 700, color: C.text, marginBottom: 8 }}>Didn't find your answer?</div>
              <p style={{ fontSize: '0.875rem', color: C.sub, marginBottom: 16 }}>Use the inquiry form above or reach us directly at <a href={`mailto:${COMPANY.email}`} style={{ color: C.blue, fontWeight: 700, textDecoration: 'none' }}>{COMPANY.email}</a></p>
              <a href={`mailto:${COMPANY.email}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 22px', background: C.blue, color: '#fff', borderRadius: 9, fontWeight: 700, textDecoration: 'none', fontSize: '0.85rem' }}>
                Email Us Directly
                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
              </a>
            </div>
          </FadeIn>
        </div>

      </div>
    </div>
    </>
  );
}
